import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doFieldSettingsSetSectionId,
  NAVIGATE_MODAL,
  doFieldSettingsReorderSections,
} from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import {
  selectOrganisationFieldSettings,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SETTINGS_FIELDS
>;

export const FieldSettingsSectionsScreen: React.FC<Props> = ({
  navigation,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId)!;
  const fieldSettings = useSelector(selectOrganisationFieldSettings);
  const isEditingKey = `iseditingorganisationfields-${organisationId}`;
  const isEditing = useSelector((state) => selectGeneric(state, isEditingKey));

  const { editable } = fieldSettings;

  const handlePressSection = React.useCallback(
    (sectionId: string) => {
      dispatch(doFieldSettingsSetSectionId(sectionId));
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.FieldSettingsSection,
          params: {},
        }),
      );
    },
    [dispatch],
  );

  const handleAddSection = () => {
    dispatch(doFieldSettingsSetSectionId(undefined));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsSection,
        params: {},
      }),
    );
  };

  const sections = React.useMemo(
    () =>
      fieldSettings.sectionIds.reduce<
        {
          id: string;
          name: string;
          countGroups: number;
          countFields: number;
          managedByOrganisation: { id: string; name: string } | undefined;
          usedByOrganisations: { id: string; name: string }[];
        }[]
      >((acc, sectionId) => {
        const section = fieldSettings.sections[sectionId];
        if (section) {
          let countGroups: number = 0;
          let countFields: number = 0;
          section.groupIds.forEach((groupId) => {
            const group = fieldSettings.groups[groupId];
            if (group) {
              countGroups += 1;
              group.fieldIds.forEach((fieldId) => {
                const field = fieldSettings.fields[fieldId];
                if (field) {
                  countFields += 1;
                }
              });
            }
          });
          acc.push({
            id: sectionId,
            name: section.name,
            usedByOrganisations: section.usedByOrganisations,
            managedByOrganisation: section.managedByOrganisation,
            countGroups,
            countFields,
          });
        }
        return acc;
      }, []),
    [
      fieldSettings.fields,
      fieldSettings.groups,
      fieldSettings.sectionIds,
      fieldSettings.sections,
    ],
  );

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          heading="Fields"
          subheading="Sections"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
      emptyChild={
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          button={{ text: 'New Section', onPress: handleAddSection }}
        />
      }
    >
      {!!sections.length && (
        <>
          <Card
            label="Sections"
            labelLinkText="Reorder"
            onLabelLinkPress={() => dispatch(doFieldSettingsReorderSections())}
            style={{ marginBottom: theme.paddingHorizontalHalf }}
          >
            {sections.map((section) => (
              <CardRow
                key={section.id}
                onPress={() => handlePressSection(section.id)}
                label={section.name}
                label2={`${pluralise(
                  section.countGroups,
                  '1 Group',
                  '{x} Groups',
                  'No Groups',
                )}, ${pluralise(section.countFields, '1 Field', '{x} Fields', 'No Fields')}${
                  section.managedByOrganisation
                    ? `\nManaged by ${section.managedByOrganisation.name}`
                    : ''
                }${
                  section.usedByOrganisations.length
                    ? `\nShared with ${pluralise(
                        section.usedByOrganisations.length,
                        '1 Organisation',
                        '{x} Organisations',
                      )}`
                    : ''
                }`}
              />
            ))}
          </Card>
          <Card label={sections.length ? '' : 'Sections'}>
            {!isEditing && editable && (
              <CardRow
                label="Add section..."
                labelColor={theme.colors.blue}
                onPress={handleAddSection}
                onAdd={handleAddSection}
              />
            )}
          </Card>
        </>
      )}
    </PageScrollView>
  );
};
